import { useEffect, useRef, useState } from "react";
import React from "react";
import { useHistory } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { FormCheck, FormEntry, FormSelect } from "components";
import { ImageModal } from "components";
import apiService from "services/api";
import { useNotificationContext } from "contexts";
import moment from "moment";
import { centreOptions } from "constants";
import { openBase64InNewTab } from "utils/openBase64InNewTab";
import { getDataURIMimeType, mimeTypes } from "utils/getDataURIMimeType";
import { FiX } from "react-icons/fi";
import { encodeImageFileAsURL } from "utils/fileToBase64";

const claimInitialState = {
	date: moment().format("YYYY-MM-DD"),
	reason: "",
	itemName: "",
	price: 0,
	centre: centreOptions[0].value,
	approvalBase64Imgs: [],
	receiptBase64Imgs: [],
	isAdvancePayment: false,
	isPettyCash: true,
	advancePaymentActualAmount: undefined,
};

const initialImageModalProps = {
	open: false,
	index: null,
	type: "approval",
};

const NewClaimPage = () => {
	const history = useHistory();
	const approvalImagesInputRef = useRef(null);
	const receiptImagesInputRef = useRef(null);
	const { createNotification } = useNotificationContext();
	const {
		register,
		handleSubmit,
		control,
		setError,
		clearErrors,
		watch,
		reset,
		formState: { errors },
	} = useForm({ mode: "onChange", defaultValues: claimInitialState });
	const [imageModalProps, setImageModalProps] = useState(initialImageModalProps);

	const {
		fields: approvalBase64Imgs,
		append: appendApprovalBase64Imgs,
		remove: removeApprovalBase64Imgs,
	} = useFieldArray({
		control,
		name: "approvalBase64Imgs",
	});
	const {
		fields: receiptBase64Imgs,
		append: appendReceiptBase64Imgs,
		remove: removeReceiptBase64Imgs,
	} = useFieldArray({
		control,
		name: "receiptBase64Imgs",
	});

	const onApprovalFileChange = async (e) => {
		if (!e.target.files.length) return;
		clearErrors("approvalBase64Imgs");
		const imgURI = await encodeImageFileAsURL(e.target.files[0]);
		appendApprovalBase64Imgs({ data: imgURI }); // use data json object because appending normal string makes the whole object spreaded
		e.target.value = "";
	};

	const isAdvancePayment = watch("isAdvancePayment");
	const isPettyCash = watch("isPettyCash");
	const chequeToFullNameRequired = React.useMemo(() => {
		return isPettyCash === "false";
	}, [isPettyCash]);
	const [isLoading, setIsLoading] = useState(false);

	const onReceiptFileChange = async (e) => {
		if (!e.target.files.length) return;
		clearErrors("receiptBase64Imgs");
		const imgURI = await encodeImageFileAsURL(e.target.files[0]);
		appendReceiptBase64Imgs({ data: imgURI });
		e.target.value = "";
	};

	const onSubmit = async (data) => {
		setIsLoading(true);
		if (!data.approvalBase64Imgs.length) return setError("approvalBase64Imgs", [{ message: "Approval image is required", type: "onChange" }]);
		if (!data.isAdvancePayment && !data.receiptBase64Imgs.length)
			return setError("receiptBase64Imgs", [{ message: "Receipt image is required", type: "onChange" }]);
		const formData = {
			...data,
			approvalBase64Imgs: approvalBase64Imgs?.map((resource) => resource.data),
			receiptBase64Imgs: receiptBase64Imgs?.map((resource) => resource.data),
		};
		apiService.staff.claims
			.create(formData)
			.then(() => {
				createNotification({
					type: "success",
					message: "Claim successfully submitted",
				});
				reset(claimInitialState);
			})
			.catch((error) => createNotification({ type: "danger", message: error.message }))
			.finally(() => setIsLoading(false));
	};

	return (
		<div className="content">
			<ImageModal open={imageModalProps.open} hideModal={() => setImageModalProps(initialImageModalProps)}>
				<img
					src={imageModalProps.type === "approval" ? approvalBase64Imgs[imageModalProps.index] : receiptBase64Imgs[imageModalProps.index]}
					alt=""
				/>
			</ImageModal>

			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="d-flex justify-content-center">
					<div className="w-75">
						<h5 className="text-center mb-4">New Claim</h5>
						{/* <FormEntry formLabel="Invoice Date" type="date" register={register} name="date" error={errors.date} required={true} /> */}
                        <FormSelect
							formLabel="Claim Period"
							register={register}
							name="claimPeriod"
							options={[
								{ value: "current", label: "Current Month" },
								{ value: "last", label: "Last Month" },
							]}
							error={errors?.claimPeriod}
							required={true}
						/>
						<FormSelect
							formLabel="Claim Type"
							register={register}
							name="isPettyCash"
							options={[
								{ value: true, label: "Petty Cash" },
								{ value: false, label: "Cheque" },
							]}
							error={errors?.isPettyCash}
							required={true}
						/>
						<FormSelect
							formLabel="Centre"
							register={register}
							name="centre"
							options={centreOptions}
							error={errors.centre}
							required={true}
						/>
						<FormSelect
							formLabel="Company"
							register={register}
							name="company"
							options={[
								{ value: "G", label: "Genius" },
								{ value: "S", label: "Scientech" },
							]}
							defaultValue="G"
							error={errors?.company}
							required={true}
						/>
						{chequeToFullNameRequired && (
							<FormEntry
								formLabel="Cheque To"
								register={() =>
									register("chequeToFullName", {
										validate: {
											minValue: (v) => v.length >= 0,
										},
										required: true,
									})
								}
								name="chequeToFullName"
								placeholder="Cheque Receiver Full Name"
								error={errors?.chequeToFullName}
								required={!chequeToFullNameRequired}
							/>
						)}
						<FormEntry
							formLabel="Item Name"
							type="text"
							register={() =>
								register("itemName", {
									required: true,
								})
							}
							placeholder="Name of item to be claimed"
						/>
						<FormEntry
							formLabel="Reason"
							type="text"
							register={() =>
								register("reason", {
									required: true,
								})
							}
							placeholder="Reason Of Claim"
						/>
						<FormEntry
							formLabel="Claim Amount (HKD)"
							type="text"
							register={() =>
								register("price", {
									required: true,
								})
							}
							placeholder="Total amount of claim"
						/>
						<FormCheck register={register} name="isAdvancePayment" formLabel={<div className="text-primary">Is advance?</div>} />
						{isAdvancePayment && (
							<FormEntry
								formLabel="Actual Claim Amount (HKD)"
								type="text"
								register={() => register("advancePaymentActualAmount")}
								placeholder="Total amount of claim"
							/>
						)}
						<div className="px-3 py-2 pb-4 mb-3 rounded-lg" style={{ border: "1px solid #ADB5DB" }}>
							<h5 className=" font-weight-bold text-white mb-1">Approval Image</h5>
							<input
								type="file"
								className="form-control d-none"
								onChange={onApprovalFileChange}
								accept="application/pdf, image/*"
								ref={approvalImagesInputRef}
							/>
							{errors.approvalBase64Imgs?.[0] && <small className="text-danger">{errors.approvalBase64Imgs?.[0]?.message}</small>}
							<div className="mt-3">
								<small
									className="border border-light rounded px-3 py-2 text-light"
									style={{ cursor: "pointer" }}
									onClick={() => {
										approvalImagesInputRef.current?.click();
									}}
								>
									Click to upload
								</small>
							</div>

							{Boolean(approvalBase64Imgs.length) && (
								<div className="row mt-3 ">
									{approvalBase64Imgs.map((resource, index) => (
										<div className="col-4" key={index}>
											<span
												className="border border-danger"
												style={{ cursor: "pointer", borderRadius: 5 }}
												onClick={() => removeApprovalBase64Imgs(index)}
											>
												<FiX className="text-danger" />
												<small className="text-danger mx-1">Remove</small>
											</span>
											<div className="mt-1">
												<small>
													{getDataURIMimeType(resource.data) !== mimeTypes.pdf ? (
														<img
															src={resource.data}
															className="img-fluid rounded text-white"
															alt="Resource Uploaded"
															onClick={() =>
																setImageModalProps({
																	open: true,
																	index,
																})
															}
														/>
													) : (
														<span
															onClick={() => openBase64InNewTab(resource.data)}
															style={{ cursor: "pointer", borderRadius: 5, padding: 2 }}
															className="border border-success text-success px-1"
														>
															View Resource {index + 1}
														</span>
													)}
												</small>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
						<div className="px-3 py-2 pb-4 rounded-lg" style={{ border: "1px solid #ADB5DB" }}>
							<h5 className=" font-weight-bold text-white mb-1">Receipt Image</h5>
							<input
								type="file"
								className="form-control d-none"
								onChange={onReceiptFileChange}
								accept="application/pdf, image/*"
								ref={receiptImagesInputRef}
							/>
							{errors.receiptBase64Imgs?.[0] && <small className="text-danger">{errors.receiptBase64Imgs?.[0]?.message}</small>}
							<div className="mt-3">
								<small
									className="border border-light rounded px-3 py-2 text-light"
									style={{ cursor: "pointer" }}
									onClick={() => {
										receiptImagesInputRef.current?.click();
									}}
								>
									Click to upload
								</small>
							</div>

							{Boolean(receiptBase64Imgs.length) && (
								<div className="row mt-3 ">
									{receiptBase64Imgs.map((resource, index) => (
										<div className="col-4" key={resource.id}>
											<span
												className="border border-danger"
												style={{ cursor: "pointer", borderRadius: 5 }}
												onClick={() => removeReceiptBase64Imgs(index)}
											>
												<FiX className="text-danger" />
												<small className="text-danger mx-1">Remove</small>
											</span>
											<div className="mt-1">
												<small>
													{getDataURIMimeType(resource.data) !== mimeTypes.pdf ? (
														<img
															src={resource.data}
															className="img-fluid rounded text-white"
															alt="Resource Uploaded"
															onClick={() =>
																setImageModalProps({
																	open: true,
																	index,
																})
															}
														/>
													) : (
														<span
															onClick={() => openBase64InNewTab(resource.data)}
															style={{ cursor: "pointer", borderRadius: 5, padding: 2 }}
															className="border border-success text-success px-1"
														>
															View Resource {index + 1}
														</span>
													)}
												</small>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
						<button className="btn btn-primary float-right" type="submit" disabled={isLoading}>
							Submit
						</button>
					</div>
				</div>
			</form>
		</div>
	);
};

export default NewClaimPage;
